import {
    AlertResolutionType,
    AlertsAcknowledgementRequest,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import {
    type EntityUpdate,
    getEntity,
    optimisticEntities,
} from 'shared/normalized_cache';
import { acknowledgeAlertsByIds } from 'utils/alert';
import { pluralize } from 'utils/string';
import { type QueueRow } from '../queue/queueRowKey';
import { ROW_ERRORS_META, useRowErrorReporter } from '../queue/useRowErrors';
import { type QueueAlert } from './queueTypes';

export type AlertEntityUpdate = Extract<EntityUpdate, { kind: 'alert' }>;

/** A saved change to an Alert. */
export type AlertChange =
    | Readonly<{ op: 'acknowledge' }>
    | Readonly<{ op: 'link'; issueId: string }>
    | Readonly<{ op: 'unlink' }>;

type AlertResolutionState = Pick<QueueAlert, 'IsAcknowledged' | 'ResolutionType'>;

/**
 * The Alert's acknowledgement and resolution after `change`, as the server records
 * them: the first action that resolves an Alert stays its resolution, except that
 * unlinking undoes a `LinkedToIssue` resolution.
 */
export function alertResolutionAfter(
    alert: AlertResolutionState,
    change: AlertChange,
): AlertResolutionState {
    return {
        IsAcknowledged: alert.IsAcknowledged || change.op === 'acknowledge',
        ResolutionType: (() => {
            if (change.op === 'unlink') {
                if (alert.ResolutionType === AlertResolutionType.LinkedToIssue) {
                    return alert.IsAcknowledged
                        ? AlertResolutionType.Acknowledged
                        : undefined;
                } else {
                    return alert.ResolutionType;
                }
            }
            if (alert.ResolutionType) {
                return alert.ResolutionType;
            }
            switch (change.op) {
                case 'acknowledge':
                    return AlertResolutionType.Acknowledged;
                case 'link':
                    return AlertResolutionType.LinkedToIssue;
            }
        })(),
    };
}

/**
 * Entity updates rendering each Alert's changes as saved now, in order, including
 * the resolution the server gives them (see `alertResolutionAfter`). Resolutions
 * follow from the Alerts as normalized queries currently hold them, so build these
 * when the save starts (e.g. in `onMutate`).
 */
export function changedAlertUpdates(
    changesByAlertId: ReadonlyMap<string, readonly AlertChange[]>,
): AlertEntityUpdate[] {
    const now = new Date();
    return changesByAlertId
        .entries()
        .map(([id, changes]): AlertEntityUpdate => {
            let fields: AlertEntityUpdate['fields'] = {};
            for (const change of changes) {
                switch (change.op) {
                    case 'acknowledge':
                        fields = {
                            ...fields,
                            IsAcknowledged: true,
                            TimeAcknowledged: now,
                        };
                        break;
                    // Alerts hold their Issue as `Issue` in most queries, and as
                    // `IssueId` in Mission Control's
                    case 'link':
                        fields = {
                            ...fields,
                            Issue: { Id: change.issueId },
                            IssueId: change.issueId,
                        };
                        break;
                    case 'unlink':
                        fields = { ...fields, Issue: undefined, IssueId: undefined };
                        break;
                }
            }

            // Only Mission Control's queries hold resolutions; an Alert that none of
            // them holds has none to update
            const alert = getEntity('alert', id);
            if (
                alert === undefined ||
                !('ResolutionType' in alert) ||
                alert.IsAcknowledged === undefined
            ) {
                return { kind: 'alert', id, fields };
            }
            const { ResolutionType } = changes.reduce(alertResolutionAfter, {
                IsAcknowledged: alert.IsAcknowledged,
                ResolutionType: alert.ResolutionType,
            });
            return { kind: 'alert', id, fields: { ...fields, ResolutionType } };
        })
        .toArray();
}

/** Entity updates rendering the Alerts as acknowledged now. */
export function acknowledgedAlertUpdates(
    alertIds: readonly string[],
): AlertEntityUpdate[] {
    return changedAlertUpdates(
        new Map(alertIds.map((id) => [id, [{ op: 'acknowledge' }] as const])),
    );
}

/**
 * Acknowledges Alerts, rendering them acknowledged everywhere while saving. They stay
 * that way after saving, without refetching Mission Control's queues: rows whose
 * triggers are all resolved show as resolved until the user refreshes.
 *
 * Failures are reported on `row`, the queue row the Alerts are listed in.
 */
export function useAcknowledgeAlerts(row: QueueRow) {
    const rowErrors = useRowErrorReporter();
    return useMutation({
        mutationKey: ['mission-control', 'acknowledge-alerts'],
        meta: { name: 'acknowledge-alerts', ...ROW_ERRORS_META },
        mutationFn: (alertIds: readonly string[]) =>
            acknowledgeAlertsByIds(
                new AlertsAcknowledgementRequest({ AlertIds: [...alertIds] }),
            ),
        onMutate: (alertIds) => optimisticEntities(acknowledgedAlertUpdates(alertIds)),
        onSuccess: () => rowErrors.clear(row.key, 'acknowledge'),
        onError: (error, alertIds) =>
            rowErrors.report({
                row,
                source: 'acknowledge',
                summary: `Failed to acknowledge ${alertIds.length} ${pluralize(alertIds.length, 'alert')}`,
                error,
            }),
    });
}
